<template>
	<div class="gmap"></div>
</template>

<script>
import gmapsInit from '../../utilities/gmaps.js';
import {
	createGoogleMapsPins,
	createStoreInfoContent,
	getRadiusOfMapView,
	getStorePosition
} from '../helpers/map.helpers';
import { get, sync } from 'vuex-pathify';

export default {
	name: 'MapWithMarkers',
	computed: {
		stores: get('stores'),
		googleApiKey: get('googleApiKey'),
		latitude: sync('latitude'),
		longitude: sync('longitude'),
		localStore: get('localStore'),
		map: sync('map'),
		radius: sync('radius'),
		searching: get('searching'),
		updateResultsOnNextMapEvent: sync('updateResultsOnNextMapEvent'),
		mapOptions() {
			if (!this.stores.length) return {};
			const store = this.stores[0];
			const center = {
				lat: store.latitude,
				lng: store.longitude
			};
			return {
				center,
				zoom: 11
			};
		},
	},
	data() {
		return {
			coolingDown: false,
			infoWindow: null,
			timeout: null
		};
	},
	methods: {
		addMapMarkers(google) {
			this.stores.forEach(store => {
				this.addMapMarker(google, store);
			});
		},
		addMapMarker(google, store) {
			const img = this.isLocalStore(store) ? 'map-pin-my-store.png' : 'map-pin.png';
			const marker = new google.maps.Marker({
				position: getStorePosition(store),
				icon: `/assets/images/stores/${img}`,
				map: this.map
			});
			marker.addListener('click', () => {
				this.handleMarkerClick(marker, store);
			});
		},
		handleMarkerClick(marker, store) {
			this.infoWindow.close();
			this.infoWindow.setContent(createStoreInfoContent(store));
			this.infoWindow.open(this.map, marker);
			this.map.panTo(getStorePosition(store));
			this.updateResults();
		},
		init() {
			gmapsInit(googleApiKey, 'gmapsCallback').then((google) => {
				if (!this.map) {
					this.map = new google.maps.Map(this.$el, this.mapOptions);
					this.initListeners();
					this.infoWindow = new google.maps.InfoWindow();
				}
				this.addMapMarkers(google);
				this.updateMapBoundary();
			});
		},
		initListeners() {
			if (!this.map) return;
			this.map.addListener('dragend', () => {
				this.infoWindow.close();
				this.updateResults();
			});
			this.map.addListener('zoom_changed', (test) => {
				this.infoWindow.close();
				this.updateResults();
			});
		},
		isLocalStore(store) {
			if (!this.localStore) return false;
			return store.id === this.localStore.id;
		},
		updateMapBoundary() {
			if (!this.map) return;
			const radius = getRadiusOfMapView(this.map);
			if (!radius) return;
			this.longitude = this.map.getCenter().lng();
			this.latitude = this.map.getCenter().lat();
			this.radius = Math.ceil(radius);
		},
		updateResults() {
			if (!this.updateResultsOnNextMapEvent) {
				// We don't want to update results when map zoom is changed programatically so
				// updateResultsOnNextMapEvent is set false in vuex before changing map zoom
				// There must be a better way to handle this, but I'm out of coffee
				this.updateResultsOnNextMapEvent = true;
				return;
			}
			if (this.timeout) clearTimeout(this.timeout);
			this.timeout = setTimeout(() => {
				this.updateMapBoundary();
				this.$store.dispatch('findByMap').then(() => {
					this.init();
				});
			}, 250);
		}
	},
	watch: {
		stores: {
			handler(newVal, prevVal) {
				if (JSON.stringify(newVal) === JSON.stringify(prevVal)) return;
				this.init();
			}
		}
	},
	mounted() {
		if (!this.stores.length) return;
		this.init();
	}
};
</script>

<style lang='less' scoped>
@import '../../../less/_mixins.less';
.gmap {
	.aspect-ratio(4, 3);
	iframe {
		position: absolute;
		top: 0; right: 0; bottom: 0; left: 0;
	}
}
</style>